import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {StateModel} from "../Models/StateModel";
import {OrderModel, OrderProductModel} from "../Models/OrderModel";
import useHttpClient from "./http/useHttpClient";
import {ProductModel} from "../Models/ProductModel";
import {useUpdatePanier} from "./useUpdatePanier";
import {useGetTva} from "./useGetTva";
import {useUpdateOrderWithoutDiscount} from "./useUpdateOrderWithoutDiscount";
import {useUpdateOrderWithPercentDiscount} from "./useUpdateOrderWithPercentDiscount";
import {useUpdateOrderWithQuantityDiscount} from "./useUpdateOrderWithQuantityDiscount";
import {useTranslation} from "react-i18next";

export const useValidateCartBeforePayment = (
    onFirst: () => void,
    onLast: () => void,
    onError: (message: string) => void
): (() => Promise<boolean>) => {
    const {t} = useTranslation('common')
    const navigate = useNavigate()
    const panier = useSelector<StateModel, OrderModel | undefined>(state => state.panier)
    const {getProducts} = useHttpClient()
    const updatePanier = useUpdatePanier()
    const getTva = useGetTva()
    const updateOrderWithoutDiscount = useUpdateOrderWithoutDiscount()
    const updateOrderWithPercentDiscount = useUpdateOrderWithPercentDiscount()
    const updateOrderWithQuantityDiscount = useUpdateOrderWithQuantityDiscount()

    const recalculateOrderProducts = (orderProducts: OrderProductModel[]): OrderProductModel[] => {
        const updatedOrderProducts: OrderProductModel[] = []
        orderProducts
            .map(p => {
                return {...p, totalPriceHT: 0, totalPriceTTC: 0, totalTva: 0, discount: 0}
            })
            .forEach((p) => {
                if (!p.discountType) {
                    updatedOrderProducts.push(updateOrderWithoutDiscount(p, p.quantity || 0))
                } else if (p.discountType === 'percent') {
                    updatedOrderProducts.push(updateOrderWithPercentDiscount(p, p.quantity || 0))
                } else {
                    updatedOrderProducts.push(updateOrderWithQuantityDiscount(p, p.quantity || 0, updatedOrderProducts))
                }
            })
        return updatedOrderProducts
    }

    const getUpdatedOrderProduct = (orderProduct: OrderProductModel, product: ProductModel, quantity: number): OrderProductModel => {
        const unitTva = getTva(product.priceTTC, product.product.tva?.rate)
        const updatedOrderProduct: OrderProductModel = {
            ...orderProduct,
            name: product.product.name,
            privateName: product.product.privateName,
            nameCategory: product.product.productCategories[0]?.name,
            nameFamily: product.product.productCategories[0]?.productCategoryFamily?.name,
            quantity,
            unitPriceHT: +((product.priceTTC - unitTva).toFixed(5)),
            unitPriceTTC: product.priceTTC,
            unitDepositPriceTTC: product.depositPriceTTC,
            tvaRate: product.product.tva?.rate,
            tvaCode: product.product.tva?.id,
            product: {
                ...orderProduct.product,
                image: product.product.image,
                categories: product.product.productCategories,
            }
        }

        if (orderProduct.eventPdvProducts && orderProduct.eventPdvProducts.length > 0) {
            const extrasMap = new Map<number, ProductModel>()
            product.eventPdvProducts?.forEach(extra => extrasMap.set(extra.product.id, extra))
            updatedOrderProduct.eventPdvProducts = orderProduct.eventPdvProducts.map(extra => {
                const latestExtra = extrasMap.get(extra.product.id)
                if (!latestExtra) {
                    return {...extra, quantity}
                }
                const extraUnitTva = getTva(latestExtra.priceTTC, latestExtra.product.tva?.rate)
                return {
                    ...extra,
                    name: latestExtra.product.name,
                    privateName: latestExtra.product.privateName,
                    nameCategory: latestExtra.product.productCategories[0]?.name,
                    nameFamily: latestExtra.product.productCategories[0]?.productCategoryFamily?.name,
                    quantity,
                    unitPriceHT: +((latestExtra.priceTTC - extraUnitTva).toFixed(5)),
                    unitPriceTTC: latestExtra.priceTTC,
                    unitDepositPriceTTC: 0,
                    tvaRate: latestExtra.product.tva?.rate,
                    tvaCode: latestExtra.product.tva?.id,
                    product: {
                        ...extra.product,
                        image: latestExtra.product.image,
                        categories: latestExtra.product.productCategories,
                    }
                }
            })
        }
        return updatedOrderProduct
    }

    return async (): Promise<boolean> => {
        if (!panier?.orderProducts || panier.orderProducts.length === 0) {
            return false
        }
        const pdvId = panier?.pdv?.id
        if (!pdvId) {
            return false
        }

        onFirst()
        try {
            const products = await getProducts(pdvId.toString())
            const productsById = new Map<number, ProductModel>()
            products.forEach(p => productsById.set(p.product.id, p))

            let hasChanged = false
            const syncedOrderProducts: OrderProductModel[] = []
            panier.orderProducts.forEach(orderProduct => {
                const product = productsById.get(orderProduct.product.id)
                if (!product) {
                    hasChanged = true
                    return
                }

                const initialQuantity = orderProduct.quantity || 0
                const availableQuantity = Math.max(0, product.stockAvailable)
                const syncedQuantity = Math.min(initialQuantity, availableQuantity)

                const currentPrice = orderProduct.unitPriceTTC || 0
                const currentDepositPrice = orderProduct.unitDepositPriceTTC || 0
                if (syncedQuantity !== initialQuantity || currentPrice !== product.priceTTC || currentDepositPrice !== product.depositPriceTTC) {
                    hasChanged = true
                }

                if (syncedQuantity > 0) {
                    syncedOrderProducts.push(getUpdatedOrderProduct(orderProduct, product, syncedQuantity))
                }
            })

            if (!hasChanged) {
                return false
            }

            const newPanier: OrderModel = {
                ...panier,
                orderProducts: recalculateOrderProducts(syncedOrderProducts)
            }
            updatePanier(newPanier)
            navigate('/panier', {state: {cartUpdatedMessage: t('cartUpdatedMessage')}})
            return true
        } catch (err: any) {
            onError(err?.response?.data?.errors?.[0]?.message || t('errors.stockCheckFailed'))
            return true
        } finally {
            onLast()
        }
    }
}
